import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import StickyBox from 'react-sticky-box';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import Breadcrumb from '../Breadcrumb';
import Sidebar from '../Sidebar';
import { Api } from '../../auth/Configs';
import { ToastConfirm, ToastError } from '../../../ui/AlertesUi';
import './ServiceUnits.css';

const emptyForm = { code: '', title: '', description: '', parentId: '' };
const normalizeSearch = (value) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('fr');

const ScopeChoices = ({ label, options, selected, onChange, emptyLabel, disabled = false }) => {
  const [query, setQuery] = useState('');
  const selectedOptions = options.filter((option) => selected.includes(option.id));
  const visibleOptions = options.filter((option) => normalizeSearch(option.label).includes(normalizeSearch(query)));
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((value) => value !== id) : [...selected, id]);

  return <div className="service-scope-picker">
    <strong className="service-scope-label">{label}</strong>
    <div className={`service-parent-selected${selectedOptions.length ? ' has-selection' : ''}`}>
      {selectedOptions.length ? selectedOptions.map((option) => <span className="service-parent-chip" key={option.id}>{option.label}<button type="button" aria-label={`Retirer ${option.label}`} onClick={() => toggle(option.id)}>×</button></span>) : <span className="service-parent-empty">{emptyLabel}</span>}
    </div>
    {disabled ? <div className="service-scope-disabled">Sélectionnez d’abord un plan.</div> : <details className="service-parent-choices">
      <summary>Choisir {label.toLocaleLowerCase('fr')}</summary>
      <div className="service-parent-panel">
        <input type="search" className="form-control" placeholder="Rechercher…" aria-label={`Rechercher dans ${label.toLocaleLowerCase('fr')}`} value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') event.preventDefault(); }} />
        <div className="service-parent-options">
          {visibleOptions.length ? visibleOptions.map((option) => <label className="service-parent-option" key={option.id}><input type="checkbox" checked={selected.includes(option.id)} onChange={() => toggle(option.id)} /><span>{option.label}</span></label>) : <span className="service-parent-no-results">Aucun résultat</span>}
        </div>
      </div>
    </details>}
  </div>;
};

const orderServices = (services) => {
  const children = new Map();
  const knownIds = new Set(services.map((service) => service.id));
  services.forEach((service) => {
    const parentId = knownIds.has(service.parentId) ? service.parentId : null;
    children.set(parentId, [...(children.get(parentId) || []), service]);
  });
  const result = [];
  const visited = new Set();
  const visit = (parentId, ancestors = [], path = []) => {
    (children.get(parentId) || [])
      .sort((a, b) => `${a.code} ${a.title}`.localeCompare(`${b.code} ${b.title}`, 'fr'))
      .forEach((service) => {
        if (visited.has(service.id)) return;
        visited.add(service.id);
        const servicePath = [...path, `${service.code} — ${service.title}`];
        result.push({ ...service, depth: ancestors.length, ancestorIds: ancestors, rootId: ancestors[0] ?? service.id, path: servicePath });
        visit(service.id, [...ancestors, service.id], servicePath);
      });
  };
  visit(null);
  services.filter((service) => !visited.has(service.id)).forEach((service) => {
    if (!visited.has(service.id)) {
      visited.add(service.id);
      const servicePath = [`${service.code} — ${service.title}`];
      result.push({ ...service, depth: 0, ancestorIds: [], rootId: service.id, path: servicePath });
      visit(service.id, [service.id], servicePath);
    }
  });
  return result;
};

export default function ServiceUnits() {
  const session = useSelector((state) => state.sessionAuth);
  const ownProfile = useSelector((state) => state.userSettings);
  const navigate = useNavigate();
  const isAdmin = (session.roles || []).some((role) => ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN'].includes(role));
  const canManageServices = isAdmin || (session.roles || []).includes('ROLE_EDITED');
  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${session.token}` } }), [session.token]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [expandedServiceIds, setExpandedServiceIds] = useState(() => new Set());
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [parentQuery, setParentQuery] = useState('');
  const parentChoiceRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [deleteService, setDeleteService] = useState(null);
  const [assignmentTarget, setAssignmentTarget] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [assignmentError, setAssignmentError] = useState('');
  const [assignmentSaving, setAssignmentSaving] = useState(false);

  const loadServices = useCallback(async () => {
    if (!canManageServices) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${Api}/services/management`, authHeaders);
      setServices(response.data?.services || []);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Impossible de charger les services producteurs.');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, canManageServices]);

  useEffect(() => { loadServices(); }, [loadServices]);

  const ordered = useMemo(() => orderServices(services), [services]);
  const visibleServices = useMemo(() => ordered.filter((service) =>
    service.ancestorIds.every((id) => expandedServiceIds.has(id))), [ordered, expandedServiceIds]);
  const childCounts = useMemo(() => {
    const counts = new Map();
    services.forEach((service) => {
      if (service.parentId !== null) counts.set(service.parentId, (counts.get(service.parentId) || 0) + 1);
    });
    return counts;
  }, [services]);
  const byId = useMemo(() => new Map(services.map((service) => [service.id, service])), [services]);
  const unavailableParents = useMemo(() => {
    const ids = new Set(editingId === null ? [] : [editingId]);
    const collectChildren = (parentId) => {
      services.filter((service) => service.parentId === parentId).forEach((service) => {
        if (!ids.has(service.id)) { ids.add(service.id); collectChildren(service.id); }
      });
    };
    if (editingId !== null) collectChildren(editingId);
    return ids;
  }, [editingId, services]);
  const availableParents = useMemo(() => ordered.filter((service) => !unavailableParents.has(service.id)), [ordered, unavailableParents]);
  const selectedParent = availableParents.find((service) => String(service.id) === form.parentId);
  const visibleParents = availableParents.filter((service) => normalizeSearch(service.path.join(' / ')).includes(normalizeSearch(parentQuery.trim())));
  const parentGroups = availableParents.filter((service) => service.depth === 0).map((root) => ({
    root,
    options: visibleParents.filter((service) => service.rootId === root.id),
  })).filter((group) => group.options.length > 0);

  const toggleService = (id) => setExpandedServiceIds((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  const openForm = (service = null, parentId = '') => {
    setEditingId(service?.id ?? null);
    setForm(service ? {
      code: service.code || '', title: service.title || '', description: service.description || '',
      parentId: service.parentId ? String(service.parentId) : '',
    } : { ...emptyForm, parentId: parentId ? String(parentId) : '' });
    setParentQuery('');
    if (parentChoiceRef.current) parentChoiceRef.current.open = false;
    setFormOpen(true);
  };

  const selectParent = (parentId) => {
    setForm((current) => ({ ...current, parentId }));
    if (parentChoiceRef.current) parentChoiceRef.current.open = false;
  };

  const saveService = async (event) => {
    event.preventDefault();
    const code = form.code.trim();
    const title = form.title.trim();
    if (!code || !title) { ToastError('Renseignez le code et le nom du service.'); return; }
    setSaving(true);
    const payload = {
      code, title, description: form.description.trim(),
      parentId: form.parentId ? Number(form.parentId) : null,
    };
    try {
      if (editingId === null) await axios.post(`${Api}/services`, payload, authHeaders);
      else await axios.patch(`${Api}/services/${editingId}`, payload, authHeaders);
      setFormOpen(false);
      ToastConfirm(editingId === null ? 'Service créé' : 'Service modifié');
      await loadServices();
    } catch (requestError) {
      ToastError(requestError.response?.data?.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteService) return;
    setSaving(true);
    try {
      await axios.delete(`${Api}/services/${deleteService.id}`, authHeaders);
      setDeleteService(null);
      setExpandedId(null);
      ToastConfirm('Service supprimé');
      await loadServices();
    } catch (requestError) {
      ToastError(requestError.response?.data?.message || 'Suppression impossible.');
    } finally {
      setSaving(false);
    }
  };

  const openAssignment = async (service, user) => {
    setAssignmentTarget({ service, user });
    setAssignment(null);
    setAssignmentError('');
    try {
      const response = await axios.get(`${Api}/services/${service.id}/users/${user.id}/access`, authHeaders);
      setAssignment({
        ...response.data,
        planIds: (response.data.planIds || []).map(Number),
        folderIds: (response.data.folderIds || []).map(Number),
      });
    } catch (requestError) {
      setAssignmentError(requestError.response?.data?.message || 'Impossible de charger cette affectation.');
    }
  };

  const saveAssignment = async () => {
    if (!assignmentTarget || !assignment) return;
    setAssignmentSaving(true);
    setAssignmentError('');
    try {
      await axios.put(`${Api}/services/${assignmentTarget.service.id}/users/${assignmentTarget.user.id}/access`, {
        planIds: assignment.planIds, folderIds: assignment.folderIds,
      }, authHeaders);
      ToastConfirm('Affectation mise à jour');
      setAssignmentTarget(null);
    } catch (requestError) {
      setAssignmentError(requestError.response?.data?.message || 'Enregistrement impossible.');
    } finally {
      setAssignmentSaving(false);
    }
  };

  return <Fragment>
    <Breadcrumb title="Administration" submenu="Services producteurs" />
    <div className="content"><div className="container"><div className="row">
      <div className="col-md-5 col-lg-4 col-xl-3 theiaStickySidebar"><StickyBox offsetTop={20} offsetBottom={20}><Sidebar /></StickyBox></div>
      <div className="col-md-7 col-lg-8 col-xl-9">
        <div className="service-admin-header">
          <div><h1>Services producteurs</h1><p>Configurez les services et consultez les utilisateurs qui leur sont affectés.</p></div>
          {canManageServices && <button className="btn btn-primary" type="button" onClick={() => openForm()}><i className="ri-add-line" aria-hidden="true" /> Nouveau service</button>}
        </div>
        {!canManageServices ? <p role="alert" className="service-admin-message">Cette page est réservée aux administrateurs et archivistes.</p>
          : loading ? <p role="status" className="service-admin-message">Chargement des services…</p>
            : error ? <p role="alert" className="service-admin-message">{error} <button type="button" className="btn btn-sm btn-outline-primary" onClick={loadServices}>Réessayer</button></p>
              : <div className="service-admin-card">
                {ordered.length === 0 ? <p className="service-admin-empty">Aucun service producteur. Créez le premier service.</p>
                  : <div className="table-responsive"><table className="table service-admin-table syidocs-data-table">
                    <thead><tr><th scope="col">Service producteur</th><th scope="col">Rattachement</th><th scope="col">Utilisateurs</th><th scope="col">Actions</th></tr></thead>
                    <tbody>{visibleServices.map((service) => <Fragment key={service.id}>
                      <tr className={service.depth === 0 ? 'service-admin-root-row' : 'service-admin-child-row'}>
                        <td data-label="Service producteur"><div className="service-admin-tree-cell" style={{ '--service-depth': service.depth }}>
                          {(childCounts.get(service.id) || 0) > 0 ? <button type="button" className="service-admin-tree-toggle" onClick={() => toggleService(service.id)} aria-expanded={expandedServiceIds.has(service.id)} aria-label={`${expandedServiceIds.has(service.id) ? 'Replier' : 'Déplier'} les sous-services de ${service.title}`}><i className={`ri-arrow-${expandedServiceIds.has(service.id) ? 'down' : 'right'}-s-line`} aria-hidden="true" /></button> : <span className="service-admin-tree-spacer" aria-hidden="true">{service.depth > 0 ? '↳' : ''}</span>}
                          <div className="service-admin-name"><strong>{service.title}</strong><span><code>{service.code}</code><small>{service.depth === 0 ? 'Service principal' : 'Sous-service'}</small></span></div>
                        </div></td>
                        <td data-label="Rattachement">{byId.get(service.parentId) ? <span className="service-admin-parent"><span>{byId.get(service.parentId).title}</span><small>{byId.get(service.parentId).code}</small></span> : <span className="service-admin-root-label">Service principal</span>}</td>
                        <td data-label="Utilisateurs"><button type="button" className="service-admin-users-button" aria-expanded={expandedId === service.id} aria-controls={`service-users-${service.id}`} onClick={() => setExpandedId(expandedId === service.id ? null : service.id)}><i className="ri-group-line" aria-hidden="true" /><span>{service.userCount} utilisateur{service.userCount > 1 ? 's' : ''}</span><i className={`ri-arrow-${expandedId === service.id ? 'up' : 'down'}-s-line`} aria-hidden="true" /></button></td>
                        <td data-label="Actions"><div className="service-admin-actions">
                          <button type="button" className="service-admin-action service-admin-action-add" onClick={() => { openForm(null, service.id); setExpandedServiceIds((current) => new Set(current).add(service.id)); }} aria-label={`Ajouter un sous-service à ${service.title}`}><i className="ri-add-line" aria-hidden="true" /> Sous-service</button>
                          <button type="button" className="syidocs-table-icon-button" onClick={() => openForm(service)} aria-label={`Modifier ${service.title}`} title={`Modifier ${service.title}`}><i className="ri-pencil-line" aria-hidden="true" /></button>
                          <button type="button" className="syidocs-table-icon-button is-danger" onClick={() => setDeleteService(service)} aria-label={`Supprimer ${service.title}`} title={`Supprimer ${service.title}`}><i className="ri-delete-bin-line" aria-hidden="true" /></button>
                        </div></td>
                      </tr>
                      {expandedId === service.id && <tr id={`service-users-${service.id}`} className="service-admin-users-row"><td colSpan="4">
                        <div className="service-admin-users-heading"><div><span className="service-admin-eyebrow">{service.code}</span><strong>Utilisateurs affectés</strong><small>{service.title}</small></div>{isAdmin && <div className="service-admin-actions"><button type="button" className="service-admin-action" onClick={() => navigate('/apps/in/users-accounts/page/1')}>Gérer les utilisateurs</button><button type="button" className="service-admin-action service-admin-action-add" onClick={() => navigate('/apps/in/users-accounts/create')}><i className="ri-user-add-line" aria-hidden="true" /> Créer un utilisateur</button></div>}</div>
                        {service.users.length === 0 ? <p className="service-admin-users-empty">Aucun utilisateur affecté à ce service.</p>
                          : <ul className="service-admin-users-list">{service.users.map((user) => <li key={user.id}>
                            <div className="service-admin-user-identity"><span className="service-admin-user-avatar" aria-hidden="true">{(user.nom || user.username || '?').charAt(0).toLocaleUpperCase('fr')}</span><span><strong>{[user.nom, user.prenoms].filter(Boolean).join(' ') || user.username}</strong><small>{user.username}{user.email ? ` · ${user.email}` : ''}</small></span><span className={`service-admin-user-status${user.etat === 'actif' ? ' is-active' : ''}`}>{user.etat}</span></div>
                            {isAdmin && <button type="button" className="service-admin-action" onClick={() => navigate(`/apps/in/users/${session.userId}/${session.sessionId}/${user.id}`)}>Modifier l’affectation <i className="ri-arrow-right-line" aria-hidden="true" /></button>}
                            {!isAdmin && (Number(ownProfile?.serviceUnit?.id) === service.id || (ownProfile?.serviceUnits || []).some((assigned) => Number(assigned.id) === service.id)) && !(user.roles || []).some((role) => ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN'].includes(role)) && <button type="button" className="service-admin-action" onClick={() => openAssignment(service, user)}>Modifier les plans et dossiers <i className="ri-arrow-right-line" aria-hidden="true" /></button>}
                          </li>)}</ul>}
                      </td></tr>}
                    </Fragment>)}</tbody>
                  </table></div>}
              </div>}
      </div>
    </div></div></div>

    <Modal isOpen={formOpen} toggle={() => !saving && setFormOpen(false)}>
      <ModalHeader toggle={() => !saving && setFormOpen(false)}>{editingId === null ? 'Nouveau service producteur' : 'Modifier le service producteur'}</ModalHeader>
      <form onSubmit={saveService}><ModalBody>
        <div className="form-group"><label htmlFor="service-code">Code du service *</label><input id="service-code" className="form-control" maxLength="64" required value={form.code} onChange={(event) => setForm({ ...form, code: event.target.value })} /><small className="form-text text-muted">Ce code est utilisé dans les références d’archive.</small></div>
        <div className="form-group"><label htmlFor="service-title">Nom du service *</label><input id="service-title" className="form-control" maxLength="255" required value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} /></div>
        <div className="form-group service-parent-picker">
          <span className="service-parent-label">Service parent</span>
          <div className={`service-parent-selected${selectedParent ? ' has-selection' : ''}`} aria-live="polite">
            {selectedParent ? <span className="service-parent-chip">{selectedParent.path.join(' / ')}<button type="button" aria-label="Retirer le service parent" onClick={() => selectParent('')}>×</button></span> : <span className="service-parent-empty">Aucun — service principal</span>}
          </div>
          <details className="service-parent-choices" ref={parentChoiceRef}>
            <summary>Choisir un service parent</summary>
            <div className="service-parent-panel">
              <input type="search" className="form-control" placeholder="Rechercher…" aria-label="Rechercher un service parent" value={parentQuery} onChange={(event) => setParentQuery(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') event.preventDefault(); }} />
              <div className="service-parent-options">
                <label className="service-parent-option"><input type="radio" name="service-parent" checked={form.parentId === ''} onChange={() => selectParent('')} /><span>Aucun — service principal</span></label>
                {parentGroups.map(({ root, options }) => <div className="service-parent-group" key={root.id}>
                  <strong className="service-parent-group-label">{root.code} — {root.title}</strong>
                  {options.map((service) => <label className="service-parent-option" key={service.id} style={{ paddingLeft: `${12 + service.depth * 20}px` }}>
                    <input type="radio" name="service-parent" checked={form.parentId === String(service.id)} onChange={() => selectParent(String(service.id))} />
                    <span>{service.depth > 0 ? '↳ ' : ''}{service.code} — {service.title}<small>{service.depth === 0 ? 'Service principal' : `Sous-service de ${service.path[service.path.length - 2]}`}</small></span>
                  </label>)}
                </div>)}
                {parentGroups.length === 0 && <span className="service-parent-no-results">Aucun résultat</span>}
              </div>
            </div>
          </details>
          <small className="form-text text-muted">Choisissez un parent pour rattacher ce service comme sous-service.</small>
        </div>
        <div className="form-group"><label htmlFor="service-description">Description</label><textarea id="service-description" className="form-control" rows="3" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></div>
      </ModalBody><ModalFooter><Button type="button" color="secondary" onClick={() => setFormOpen(false)} disabled={saving}>Annuler</Button><Button type="submit" color="primary" disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</Button></ModalFooter></form>
    </Modal>

    <Modal isOpen={!!deleteService} toggle={() => !saving && setDeleteService(null)}>
      <ModalHeader toggle={() => !saving && setDeleteService(null)}>Supprimer un service</ModalHeader>
      <ModalBody>Supprimer « {deleteService?.title} » ? Un service utilisé par des utilisateurs, des sous-services ou des archives ne peut pas être supprimé.</ModalBody>
      <ModalFooter><Button color="secondary" onClick={() => setDeleteService(null)} disabled={saving}>Annuler</Button><Button color="danger" onClick={confirmDelete} disabled={saving}>{saving ? 'Suppression…' : 'Supprimer'}</Button></ModalFooter>
    </Modal>

    <Modal isOpen={!!assignmentTarget} toggle={() => !assignmentSaving && setAssignmentTarget(null)} size="lg">
      <ModalHeader toggle={() => !assignmentSaving && setAssignmentTarget(null)}>Affectation de {assignmentTarget?.user.nom} {assignmentTarget?.user.prenoms}</ModalHeader>
      <ModalBody>
        <p>Service : <strong>{assignmentTarget?.service.title}</strong>. Vous pouvez affecter cet utilisateur uniquement aux plans et dossiers de votre périmètre.</p>
        {assignmentError && <p className="service-admin-assignment-error" role="alert">{assignmentError}</p>}
        {!assignment && !assignmentError && <p role="status">Chargement des accès…</p>}
        {assignment?.currentAccessExceedsScope && <p className="service-admin-assignment-notice">Les accès actuels de cet utilisateur dépassent votre périmètre. L’enregistrement les remplacera par les plans et dossiers sélectionnés ici.</p>}
        {assignment && <div className="service-scope-grid">
          <ScopeChoices label="Plans autorisés" options={assignment.plans.map((plan) => ({ id: plan.id, label: `${plan.code} — ${plan.title}` }))} selected={assignment.planIds} emptyLabel={assignment.requiresPlans ? 'Aucun plan sélectionné' : 'Tous les plans'} onChange={(planIds) => setAssignment((current) => ({ ...current, planIds, folderIds: planIds.length ? current.folderIds.filter((id) => current.folders.some((folder) => folder.id === id && planIds.includes(folder.planId))) : current.requiresPlans ? [] : current.folderIds }))} />
          <ScopeChoices label="Dossiers autorisés" options={assignment.folders.filter((folder) => assignment.planIds.length === 0 || assignment.planIds.includes(folder.planId)).map((folder) => ({ id: folder.id, label: `${folder.path || folder.reference} — ${folder.title}` }))} selected={assignment.folderIds} emptyLabel={assignment.requiresFolders ? 'Aucun dossier sélectionné' : 'Tous les dossiers des plans sélectionnés'} disabled={assignment.requiresPlans && assignment.planIds.length === 0} onChange={(folderIds) => setAssignment((current) => ({ ...current, folderIds }))} />
        </div>}
      </ModalBody>
      <ModalFooter><Button color="secondary" onClick={() => setAssignmentTarget(null)} disabled={assignmentSaving}>Annuler</Button><Button color="primary" onClick={saveAssignment} disabled={!assignment || assignmentSaving}>{assignmentSaving ? 'Enregistrement…' : 'Enregistrer'}</Button></ModalFooter>
    </Modal>
  </Fragment>;
}
